// =============================================================================
// components/markets/CoinIcon.tsx — real coin logo with monogram fallback.
//
// Triage P1 (IMG_5848): token icons were two-letter TEXT monograms. The primary
// data source (/market/tokens) carries CoinGecko logo URLs — render the real
// logo, and only fall back to the lavender monogram when the image is missing
// or fails to load (e.g. the /crypto/prices fallback source has no images).
// =============================================================================
import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import CachedImage from '../ui/CachedImage';
import { colors, fonts } from '../../constants/theme';

export interface CoinIconProps {
  symbol: string;
  image?: string | null;
  size?: number;
}

export default function CoinIcon({ symbol, image, size = 36 }: CoinIconProps) {
  const [failed, setFailed] = useState(false);
  const round = { width: size, height: size, borderRadius: size / 2 };

  if (image && !failed) {
    return (
      <View style={[styles.wrap, round]}>
        <CachedImage
          source={{ uri: image }}
          style={round}
          onError={() => setFailed(true)}
          accessibilityIgnoresInvertColors
        />
      </View>
    );
  }

  return (
    <View style={[styles.wrap, styles.fallback, round]}>
      <Text style={[styles.monogram, { fontSize: Math.max(10, size * 0.34) }]}>
        {(symbol || '?').slice(0, 2).toUpperCase()}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    overflow: 'hidden',
  },
  fallback: {
    backgroundColor: 'rgba(108,92,231,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(108,92,231,0.18)',
  },
  monogram: {
    ...fonts.bold,
    color: colors.primary,
  },
});
